import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, FlaskConical, Send, OctagonX, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { ConfirmDialog } from "../client-groups/ConfirmDialog";

export const DeliveryBadge = ({ mode, size = "md", testid }) => {
  const live = mode === "live";
  const cls = size === "lg" ? "text-sm px-3.5 py-1.5 gap-1.5" : "text-xs px-2.5 py-1 gap-1";
  return (
    <span data-testid={testid}
      className={`inline-flex items-center font-bold tracking-wide rounded-md border ${cls} ${live ? "bg-green-600 text-white border-green-700" : "bg-amber-300 text-amber-950 border-amber-400"}`}>
      {live ? <Radio className={size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5"} /> : <FlaskConical className={size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5"} />}
      {live ? "LIVE" : "TEST"}
    </span>
  );
};

let _testInbox = null;
const testInbox = async () => {
  if (_testInbox === null) {
    const { data } = await api.get("/comms/delivery-modes");
    _testInbox = data.test_recipient || "";
  }
  return _testInbox;
};

// One click: sample-data copy of the template to the test inbox.
export const SendSampleButton = ({ templateKey, className = "" }) => {
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      const to = await testInbox();
      if (!to) { toast.error("No test inbox configured (EMAIL_TEST_RECIPIENT)"); return; }
      const { data } = await api.post(`/comms/templates/${templateKey}/send-test`, { recipient: to });
      if (data.status === "sent") toast.success(`Sample sent to ${to}`);
      else toast.warning(`Not sent: ${data.reason || data.error || data.status}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <button onClick={send} disabled={busy} data-testid={`send-sample-${templateKey}`}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border border-[#0066FF]/40 text-[#0066FF] hover:bg-[#0066FF]/5 disabled:opacity-50 ${className}`}>
      <Send className="w-3.5 h-3.5" />{busy ? "Sending…" : "Send test"}
    </button>
  );
};

export const KillSwitchBar = () => {
  const [ks, setKs] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/comms/kill-switch").then(({ data }) => setKs(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!ks) return null;
  const off = ks.outgoing_disabled;
  const flip = async (note) => {
    try {
      await api.post("/comms/kill-switch", { outgoing_disabled: !off, note });
      toast.success(off ? "Outgoing email re-enabled" : "ALL outgoing email disabled");
      load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };
  return (
    <>
      <div data-testid="kill-switch-bar"
        className={`mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 ${off ? "bg-red-600 border-red-700 text-white" : "bg-white border-slate-200 text-slate-700"}`}>
        <div className="flex items-center gap-2 text-sm">
          <OctagonX className={`w-5 h-5 ${off ? "text-white" : "text-red-600"}`} />
          {off ? (
            <span data-testid="kill-switch-status"><b>ALL OUTGOING EMAIL IS DISABLED.</b> Nothing is being sent, not even test or sample emails. Disabled by {ks.updated_by}{ks.note ? ` — “${ks.note}”` : ""}.</span>
          ) : (
            <span data-testid="kill-switch-status">Emergency stop: outgoing email is <b>running</b>. Per-email Live/Test settings apply.</span>
          )}
        </div>
        <button onClick={() => setOpen(true)} data-testid="kill-switch-toggle"
          className={`text-sm font-semibold px-4 py-1.5 rounded-lg ${off ? "bg-white text-red-700 hover:bg-red-50" : "bg-red-600 text-white hover:bg-red-700"}`}>
          {off ? "Re-enable outgoing email" : "Disable all outgoing emails"}
        </button>
      </div>
      <ConfirmDialog open={open} onOpenChange={setOpen} testid="kill-switch-dialog" requireNote danger={!off}
        title={off ? "Re-enable outgoing email?" : "Stop ALL outgoing email?"}
        description={off ? "Emails resume immediately. Each one follows its own Live/Test setting. Emails blocked while the switch was on are not resent."
          : "Every email stops immediately: live, test, samples, password resets and login links. Blocked emails are logged, not queued. Your per-email settings are kept."}
        confirmLabel={off ? "Re-enable" : "Disable everything"} notePlaceholder="Reason (required)" onConfirm={flip} />
    </>
  );
};

export const SenderHealthBar = () => {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (refresh) => {
    setBusy(true);
    api.get("/comms/sender-health", { params: refresh ? { refresh: true } : {} })
      .then(({ data }) => setH(data)).catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(() => { load(false); }, []);
  if (!h) return null;
  return (
    <div data-testid="sender-health-bar"
      className={`mb-3 flex flex-wrap items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${h.ok ? "bg-white border-slate-200 text-slate-700" : "bg-amber-50 border-amber-400 text-amber-900"}`}>
      <div className="flex items-start gap-2">
        {h.ok ? <ShieldCheck className="w-5 h-5 text-green-600 shrink-0" /> : <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />}
        <div>
          <div data-testid="sender-health-status">
            Sender <code>{h.sender_email}</code>: {h.ok ? <b className="text-green-700">verified sending domain</b> : <b>needs attention</b>}
            <span className="text-xs text-slate-500"> · checked {new Date(h.checked_at).toLocaleString()}</span>
          </div>
          {!h.ok && <ul className="mt-1 list-disc pl-5 text-xs" data-testid="sender-health-issues">{h.issues.map((i) => <li key={i}>{i}</li>)}</ul>}
        </div>
      </div>
      <button onClick={() => load(true)} disabled={busy} data-testid="sender-health-recheck"
        className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-50">
        <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />Re-check
      </button>
    </div>
  );
};
